import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import confetti from "canvas-confetti";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TOPIC_ICONS } from "@/lib/topics";
import { ArrowLeft, ArrowUpRight, Trophy } from "lucide-react";
import { fireConfetti } from "@/components/Confetti";
import LiveAvatarVideo from "@/components/LiveAvatarVideo";
import AgentAvatar from "@/components/AgentAvatar";
// InstructionModal is shown in BattleRegistrationModal before this page loads

const TOPIC_AGENT = {
  general_knowledge: "chanakya",
  know_your_bihar:   "ashoka",
  science:           "kalpana",
  hindi_cinema:      "bhawna",
};

const TOTAL_ROUNDS = 10;
const PLAYER_TIME  = 30; // seconds per MCQ

const LEVEL_LABELS = { L0: "Quarter-Final", L1: "Semi-Final", L2: "Final", L3: "Bronze Match" };
const LEVEL_NEXT   = { L0: "Semi-Final", L1: "Final", L2: null, L3: null };

export default function Battle() {
  const navigate = useNavigate();
  const [players] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("proctorly_battle") || "null"); }
    catch { return null; }
  });
  const [topics, setTopics] = useState([]);
  const [agents, setAgents] = useState({});

  // Initialise from sessionStorage if battle settings were chosen on the registration modal
  const [agentGender, setAgentGender] = useState(() => players?.agentGender || "female");
  const [battleMode, setBattleMode]   = useState(() => players?.battleMode  || "ai_voice");
  const [p1Lang, setP1Lang] = useState(() => players?.p1Lang || "en");
  const [p2Lang, setP2Lang] = useState(() => players?.p2Lang || "en");

  // If settings already came from the modal, jump straight to topic selection
  const [stage, setStage] = useState(() => players?.battleMode ? "p1_topic" : "mode");

  // Live Avatar (battle host) refs
  const liveAvatarRef = useRef(null);
  const [avatarConnected, setAvatarConnected] = useState(false);
  const [p1Topic, setP1Topic] = useState(null);
  const [p2Topic, setP2Topic] = useState(null);
  const [room, setRoom]     = useState(null);
  const [currentRound, setCurrentRound] = useState(null);
  const [p1Option, setP1Option] = useState("");
  const [p2Option, setP2Option] = useState("");
  const [count, setCount]   = useState(3);
  const [scores, setScores] = useState({ p1: 0, p2: 0 });
  const [winner, setWinner] = useState(null);
  const [isScoring, setIsScoring] = useState(false); // brief scoring flash, no stage change
  const [config, setConfig] = useState({ linkedin_url: "", whatsapp_url: "" });
  const [claim, setClaim]   = useState({ linkedin: false, whatsapp: false });
  const [claimDone, setClaimDone] = useState(false);

  useEffect(() => {
    if (!players) { toast.error("Register both players first"); navigate("/"); return; }
    api.topics().then((r) => setTopics(r.topics)).catch(() => {});
    api.agents().then((r) => setAgents(r.agents || {})).catch(() => {});
    api.config().then(setConfig).catch(() => {});
  }, [navigate, players]);

  // Countdown 3-2-1 → fetch next round
  useEffect(() => {
    if (stage !== "countdown") return;
    setCount(3);
    let n = 3;
    const id = setInterval(() => {
      n -= 1;
      if (n <= 0) { clearInterval(id); fetchNextRound(); }
      else setCount(n);
    }, 800);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Battle loading: wait for Live Avatar to connect (or 8s timeout), then → countdown
  useEffect(() => {
    if (stage !== "battle_loading") return;
    if (battleMode === "live_avatar") {
      // Wait up to 8s for Live Avatar to connect
      if (avatarConnected) {
        const id = setTimeout(() => setStage("countdown"), 800);
        return () => clearTimeout(id);
      }
      const id = setTimeout(() => setStage("countdown"), 8000);
      return () => clearTimeout(id);
    }
    // AI Voice: 4s cinematic loading then countdown
    const id = setTimeout(() => setStage("countdown"), 4000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, avatarConnected, battleMode]);

  const pickP1Topic = (t) => { setP1Topic(t); setStage("p2_topic"); };

  const pickP2Topic = async (t) => {
    setP2Topic(t);
    try {
      let r;
      if (players?.room_id) {
        try {
          await api.battleSetPlayerTopics(players.room_id, p1Topic.id, t.id, p1Lang, agentGender, p1Lang, p2Lang);
          r = await api.battleGet(players.room_id);
        } catch {
          r = await api.battleCreate({ topic: p1Topic.id, player1: players.player1, player2: players.player2 });
          await api.battleSetPlayerTopics(r.room_id, p1Topic.id, t.id, p1Lang, agentGender, p1Lang, p2Lang);
        }
      } else {
        r = await api.battleCreate({ topic: p1Topic.id, player1: players.player1, player2: players.player2 });
        await api.battleSetPlayerTopics(r.room_id, p1Topic.id, t.id, p1Lang, agentGender, p1Lang, p2Lang);
      }
      setRoom(r);
      setStage("battle_loading"); // go to cinematic loading before countdown
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start battle");
    }
  };

  const fetchNextRound = async () => {
    if (!room) return;
    setP1Option(""); setP2Option("");
    try {
      const r = await api.battleNext(room.room_id);
      setCurrentRound(r);
      setStage("p1_qa");
    } catch {
      toast.error("Could not load round");
    }
  };

  const scoreCurrentRound = async () => {
    if (!room || !currentRound) return;
    setIsScoring(true);
    try {
      const r = await api.battleScore(room.room_id, {
        round_num: currentRound.round_num,
        p1_option: p1Option,
        p2_option: p2Option,
        p1_answer: "",
        p2_answer: "",
      });
      setScores({ p1: r.p1_total, p2: r.p2_total });

      if (currentRound.round_num >= TOTAL_ROUNDS) {
        const w = await api.battleFinish(room.room_id);
        setWinner(w);
        setIsScoring(false);
        setStage("winner");
        // Clear stale room data so next battle starts fresh
        try { sessionStorage.removeItem("proctorly_battle"); } catch {}
        setTimeout(fireConfetti, 200);
      } else {
        // Skip 3-2-1 countdown between rounds — go straight to next question
        setIsScoring(false);
        fetchNextRound();
      }
    } catch {
      toast.error("Scoring failed");
      setIsScoring(false);
      setStage("p2_qa");
    }
  };

  const submitClaim = async (playerNum) => {
    try {
      await api.battleClaim(room.room_id, {
        player_num: playerNum, linkedin_followed: claim.linkedin, whatsapp_joined: claim.whatsapp,
      });
      setClaimDone(true);
      toast.success("Gift claim sent to Kiba Labs!");
    } catch { toast.error("Claim failed"); }
  };

  if (!players) return null;
  const p1 = players.player1, p2 = players.player2;
  const p1Question  = currentRound?.p1_question || currentRound?.question || "";
  const p2Question  = currentRound?.p2_question || currentRound?.question || "";
  const p1Options   = currentRound?.p1_options || [];
  const p2Options   = currentRound?.p2_options || [];
  const p1AgentKey  = p1Topic ? (TOPIC_AGENT[p1Topic.id] || "chanakya") : "chanakya";
  const p2AgentKey  = p2Topic ? (TOPIC_AGENT[p2Topic.id] || "chanakya") : "chanakya";
  const p1AgentInfo = agents[p1AgentKey] || {};
  const p2AgentInfo = agents[p2AgentKey] || {};

  // Compute avatar image for a given topic + gender + lang
  const getAvatarSrc = (topicId, gender, langCode) => {
    const grp = TOPIC_GROUP_MAP_B[topicId] || "A";
    const langKey = langCode === "en" ? "en" : "hi";
    const comboKey = `${grp}-${gender}-${langKey}`;
    return COMBO_AVATAR_B[comboKey] || LOCAL_AVATARS[TOPIC_AGENT[topicId] || "chanakya"] || "";
  };
  const p1AvatarSrc = getAvatarSrc(p1Topic?.id, agentGender, p1Lang);
  const p2AvatarSrc = getAvatarSrc(p2Topic?.id, agentGender, p2Lang);

  return (
    <div className="min-h-screen text-white" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />

      {/* Live Avatar — full-height left panel */}
      {battleMode === "live_avatar" && (stage === "battle_loading" || stage === "countdown" || stage === "p1_qa" || stage === "p2_qa") && (
        <div style={{
          position: "fixed", top: 65, left: 0, bottom: 0,
          width: "28vw", minWidth: 220, maxWidth: 400, zIndex: 35,
          overflow: "hidden",
          borderRight: "2px solid rgba(230,57,70,0.4)",
          boxShadow: "6px 0 50px rgba(230,57,70,0.25)",
          background: "#000",
        }}>
          <LiveAvatarVideo
            ref={liveAvatarRef}
            topic="battle"
            lang="en"
            gender={agentGender}
            fallbackSrc="/kiba.png"
            onStatusChange={(s) => setAvatarConnected(s === "connected")}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }}
          />
          {/* Bottom label */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            background: "linear-gradient(transparent, rgba(6,10,31,0.97))",
            padding: "32px 12px 10px", textAlign: "center", pointerEvents: "none",
          }}>
            <div style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(230,57,70,0.8)" }}>
              {avatarConnected ? "🎥 Proctorly AI · Live" : "🎥 Connecting…"}
            </div>
          </div>
        </div>
      )}

      {/* ── QA stages: full-viewport, no container ── */}
      {(stage === "p1_qa" || stage === "p2_qa") && currentRound && (
        <QuestionAndAnswer
          key={stage === "p1_qa" ? "p1" : "p2"}
          playerNum={stage === "p1_qa" ? 1 : 2}
          player={stage === "p1_qa" ? p1 : p2}
          opponent={stage === "p1_qa" ? p2 : p1}
          agentKey={stage === "p1_qa" ? p1AgentKey : p2AgentKey}
          agentInfo={stage === "p1_qa" ? p1AgentInfo : p2AgentInfo}
          question={stage === "p1_qa" ? p1Question : p2Question}
          options={stage === "p1_qa" ? p1Options : p2Options}
          topic={stage === "p1_qa" ? p1Topic : p2Topic}
          lang={stage === "p1_qa" ? p1Lang : p2Lang}
          agentGender={agentGender}
          p1AvatarSrc={p1AvatarSrc} p2AvatarSrc={p2AvatarSrc}
          p1AgentKey={p1AgentKey}   p2AgentKey={p2AgentKey}
          battleMode={battleMode}
          liveAvatarRef={liveAvatarRef}
          avatarConnected={avatarConnected}
          roundNum={currentRound.round_num}
          selectedOption={stage === "p1_qa" ? p1Option : p2Option}
          onSelect={stage === "p1_qa" ? setP1Option : setP2Option}
          onSubmit={stage === "p1_qa" ? () => setStage("p2_qa") : scoreCurrentRound}
          scores={scores}
        />
      )}

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 relative">
        <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-15 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(230,57,70,0.6), transparent 60%)" }} />

        {stage !== "p1_qa" && stage !== "p2_qa" && (
          <button
            onClick={() => {
              if (stage === "p2_topic") { setStage("p1_topic"); return; }
              if (stage === "p1_topic") { setStage("mode"); return; }
              navigate("/");
            }}
            className="relative z-10 font-mono-kl text-xs uppercase tracking-widest text-white/60 hover:text-white inline-flex items-center gap-2 mb-6"
            data-testid="battle-back">
            <ArrowLeft className="w-4 h-4" />
            {stage === "p2_topic" ? "Back · Player 1 Topic" : stage === "p1_topic" ? "Back · Setup" : "Back to home"}
          </button>
        )}

        {stage !== "p1_qa" && stage !== "p2_qa" && stage !== "mode" && stage !== "battle_loading" && (
          <Scoreboard p1={p1} p2={p2} scores={scores} round={currentRound?.round_num || 0}
            p1Topic={p1Topic?.label} p2Topic={p2Topic?.label} />
        )}

        {/* ── MODE + LANGUAGE SETUP ─────────────────────────────────────────── */}
        {stage === "mode" && (
          <div className="mt-12 relative z-10 slide-up flex flex-col items-center text-center">
            <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-4">
              Step 1 of 3 · Battle Setup
            </div>
            <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight text-white">
              Choose your<br /><span style={{ color: "var(--kl-red)" }}>mode.</span>
            </h1>

            {/* Mode selector */}
            <div className="mt-10 w-full max-w-lg">
              <div className="font-mono-kl text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3 text-left">Battle Mode</div>
              <div className="flex gap-4">
                {[
                  { code: "live_avatar", label: "🎥 Live Avatar", sub: "Real-time AI face · English only", icon: "🎥" },
                  { code: "ai_voice",    label: "🎙 AI Voice",    sub: "Hindi · Bhojpuri · English", icon: "🎙" },
                ].map(({ code, label, sub }) => (
                  <button key={code} onClick={() => setBattleMode(code)}
                    className="flex-1 flex flex-col items-center gap-2 px-6 py-6 rounded-xl border transition-all"
                    style={battleMode === code
                      ? { background: "rgba(230,57,70,0.12)", border: "2px solid var(--kl-red)", color: "#fff" }
                      : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
                    }>
                    <span className="font-display text-2xl uppercase">{label}</span>
                    <span className="font-mono-kl text-[10px] uppercase tracking-widest opacity-60">{sub}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Per-player language (AI Voice only) */}
            {battleMode === "ai_voice" && (
              <div className="mt-8 w-full max-w-lg grid grid-cols-2 gap-6">
                {[
                  { label: p1 ? `${p1.name.split(" ")[0]}'s Language` : "Player 1 Language", lang: p1Lang, setLang: setP1Lang },
                  { label: p2 ? `${p2.name.split(" ")[0]}'s Language` : "Player 2 Language", lang: p2Lang, setLang: setP2Lang },
                ].map(({ label, lang: curLang, setLang }) => (
                  <div key={label}>
                    <div className="font-mono-kl text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 text-left">{label}</div>
                    <div className="flex flex-col gap-2">
                      {[
                        { code: "en",  label: "English" },
                        { code: "hi",  label: "हिन्दी" },
                        { code: "bho", label: "भोजपुरी" },
                      ].map(({ code, label: lbl }) => (
                        <button key={code} onClick={() => setLang(code)}
                          className="px-4 py-3 rounded-lg border text-left transition-all font-display text-lg uppercase"
                          style={curLang === code
                            ? { background: "rgba(230,57,70,0.12)", border: "2px solid var(--kl-red)", color: "#fff" }
                            : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
                          }>{lbl}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {battleMode === "live_avatar" && (
              <p className="mt-6 font-mono-kl text-xs text-white/40 uppercase tracking-widest">
                Live Avatar speaks English · Both players receive questions in English
              </p>
            )}

            {/* Voice gender */}
            <div className="mt-8 w-full max-w-lg">
              <div className="font-mono-kl text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3 text-left">Agent Voice</div>
              <div className="flex gap-4">
                {[
                  { code: "female", label: "Female" },
                  { code: "male",   label: "Male" },
                ].map(({ code, label }) => (
                  <button key={code} onClick={() => setAgentGender(code)}
                    className="flex-1 py-3 rounded-xl border transition-all font-display text-xl uppercase"
                    style={agentGender === code
                      ? { background: "rgba(230,57,70,0.12)", border: "2px solid var(--kl-red)", color: "#fff" }
                      : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
                    }>{label}</button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setStage("p1_topic")}
              className="mt-10 px-12 py-4 rounded-xl font-display text-xl uppercase tracking-wide transition-all"
              style={{ background: "var(--kl-red)", color: "#fff", border: "2px solid var(--kl-red)" }}
            >
              Continue → Pick Topics
            </button>
          </div>
        )}

        {(stage === "p1_topic" || stage === "p2_topic") && (
          <TopicPicker stage={stage} p1={p1} p2={p2} p1Topic={p1Topic} topics={topics}
            lang={stage === "p1_topic" ? p1Lang : p2Lang}
            agentGender={agentGender} battleMode={battleMode}
            onPick={stage === "p1_topic" ? pickP1Topic : pickP2Topic} />
        )}

        {/* ── CINEMATIC BATTLE LOADING ─────────────────────────────────────── */}
        {stage === "battle_loading" && (
          <BattleLoadingScreen
            p1={p1} p2={p2}
            p1AvatarSrc={p1AvatarSrc} p2AvatarSrc={p2AvatarSrc}
            p1Topic={p1Topic} p2Topic={p2Topic}
            battleMode={battleMode} avatarConnected={avatarConnected}
          />
        )}

        {stage === "countdown" && (
          <div className="text-center py-32 relative z-10" data-testid="battle-countdown">
            <div className="font-display text-[16rem] leading-none score-pop text-[color:var(--kl-red)]">{count}</div>
            <div className="font-mono-kl uppercase tracking-widest text-sm text-white/60">
              Question {(currentRound?.round_num || 0) + 1} of {TOTAL_ROUNDS} starting…
            </div>
          </div>
        )}

        {/* Scoring flash badge — floats over the page, no layout shift */}
        {isScoring && (
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 200, pointerEvents: "none",
            background: "rgba(6,10,24,0.88)",
            border: "1.5px solid rgba(230,57,70,0.5)",
            borderRadius: 16,
            padding: "14px 36px",
            backdropFilter: "blur(12px)",
            boxShadow: "0 0 40px rgba(230,57,70,0.25)",
            animation: "scoring-pop 0.2s ease-out",
          }}>
            <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: "0.12em", textTransform: "uppercase" }}>
              Scoring…
            </div>
          </div>
        )}

        {stage === "winner" && winner && (
          <WinnerScreen winner={winner} p1={p1} p2={p2}
            onClaim={() => setStage("claim")} navigate={navigate} />
        )}

        {stage === "claim" && winner && (
          <ClaimScreen winner={winner} p1={p1} p2={p2} config={config}
            claim={claim} setClaim={setClaim} submitClaim={submitClaim}
            claimDone={claimDone} navigate={navigate} />
        )}
      </div>

      <style>{`
        @keyframes battle-slide-left  { from { transform: translateX(-120px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes battle-slide-right { from { transform: translateX(120px);  opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes battle-vs-pop      { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes battle-pulse-ring  { 0%, 100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }
        @keyframes scoring-pop        { from { opacity: 0; transform: translate(-50%, -50%) scale(0.85); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cinematic Battle Loading Screen
// ─────────────────────────────────────────────────────────────────────────────
function BattleLoadingScreen({ p1, p2, p1AvatarSrc, p2AvatarSrc, p1Topic, p2Topic, battleMode, avatarConnected }) {
  const [dots, setDots] = useState(".");
  useEffect(() => {
    const id = setInterval(() => setDots((d) => d.length >= 3 ? "." : d + "."), 500);
    return () => clearInterval(id);
  }, []);

  const p1Color = "#E63946";
  const p2Color = "#7F77DD";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "var(--kl-navy-deep)" }}>
      {/* Background glow */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(230,57,70,0.08), transparent 70%)",
        pointerEvents: "none",
      }} />

      {/* ── Scrolling avatar mosaic (same cinematic effect as Blitz/Chat loading) ── */}
      <style>{`
        @keyframes bl-mosaic { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes battle-slide-left  { from { transform: translateX(-120px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes battle-slide-right { from { transform: translateX(120px);  opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes battle-vs-pop      { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes battle-pulse-ring  { 0%, 100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }
      `}</style>
      <div style={{
        position: "absolute", top: 0, bottom: 0, left: 0, width: "100%",
        overflow: "hidden", opacity: 0.18, zIndex: 0, pointerEvents: "none",
      }}>
        <div style={{
          display: "flex", gap: 10, height: "100%", alignItems: "center",
          width: "max-content",
          animation: "bl-mosaic 50s linear infinite",
        }}>
          {[
            p1AvatarSrc, p2AvatarSrc,
            "/male-1.png","/male-2.png","/male-3.png","/male-4.png",
            "/fe-1.png","/fe-2.png","/fe-3.png","/fe-4.png","/fe-6.svg","/33-boy.png",
            p1AvatarSrc, p2AvatarSrc,
            "/male-1.png","/male-2.png","/male-3.png","/male-4.png",
            "/fe-1.png","/fe-2.png","/fe-3.png","/fe-4.png","/fe-6.svg","/33-boy.png",
            p1AvatarSrc, p2AvatarSrc,
            "/male-1.png","/male-2.png","/male-3.png","/male-4.png",
            "/fe-1.png","/fe-2.png","/fe-3.png","/fe-4.png","/fe-6.svg","/33-boy.png",
          ].map((src, i) => (
            <img key={i} src={src} alt="" style={{
              height: "80vh", width: "auto", objectFit: "cover",
              borderRadius: 8, filter: "grayscale(70%) brightness(0.5)",
              flexShrink: 0,
            }} />
          ))}
        </div>
      </div>

      <div className="relative w-full max-w-5xl mx-auto flex items-center justify-center" style={{ height: 520 }}>

        {/* P1 Avatar — slides in from left */}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: "36%", overflow: "hidden",
          animation: "battle-slide-left 0.7s cubic-bezier(0.22,1,0.36,1) both",
          borderRadius: "0 0 0 0",
        }}>
          <img src={p1AvatarSrc} alt={p1?.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block",
              filter: "brightness(0.7) grayscale(0.2)" }} />
          {/* Right fade */}
          <div style={{ position: "absolute", inset: 0,
            background: "linear-gradient(to right, transparent 50%, var(--kl-navy-deep) 100%)",
            pointerEvents: "none" }} />
          {/* Glow ring */}
          <div style={{ position: "absolute", inset: 0, boxShadow: `inset 0 0 60px ${p1Color}40`, pointerEvents: "none",
            animation: "battle-pulse-ring 1.8s ease-in-out infinite" }} />
          {/* Name */}
          <div style={{ position: "absolute", bottom: 20, left: 0, right: 0, textAlign: "center" }}>
            <div className="font-display text-2xl uppercase text-white">{p1?.name?.split(" ")[0]}</div>
            {p1Topic && <div className="font-mono-kl text-[10px] uppercase tracking-widest mt-1" style={{ color: p1Color }}>{p1Topic.short || p1Topic.label}</div>}
          </div>
        </div>

        {/* P2 Avatar — slides in from right */}
        <div style={{
          position: "absolute", right: 0, top: 0, bottom: 0,
          width: "36%", overflow: "hidden",
          animation: "battle-slide-right 0.7s cubic-bezier(0.22,1,0.36,1) both",
        }}>
          <img src={p2AvatarSrc} alt={p2?.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block",
              filter: "brightness(0.7) grayscale(0.2)" }} />
          {/* Left fade */}
          <div style={{ position: "absolute", inset: 0,
            background: "linear-gradient(to left, transparent 50%, var(--kl-navy-deep) 100%)",
            pointerEvents: "none" }} />
          <div style={{ position: "absolute", inset: 0, boxShadow: `inset 0 0 60px ${p2Color}40`, pointerEvents: "none",
            animation: "battle-pulse-ring 1.8s ease-in-out 0.4s infinite" }} />
          <div style={{ position: "absolute", bottom: 20, left: 0, right: 0, textAlign: "center" }}>
            <div className="font-display text-2xl uppercase text-white">{p2?.name?.split(" ")[0]}</div>
            {p2Topic && <div className="font-mono-kl text-[10px] uppercase tracking-widest mt-1" style={{ color: p2Color }}>{p2Topic.short || p2Topic.label}</div>}
          </div>
        </div>

        {/* Center: VS + status */}
        <div className="relative z-10 flex flex-col items-center gap-4"
          style={{ animation: "battle-vs-pop 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s both" }}>
          <div className="font-mono-kl text-[10px] uppercase tracking-[0.4em] text-white/40">
            {battleMode === "live_avatar" && !avatarConnected ? "Live Avatar Connecting…" : "Preparing Battle"}
          </div>
          <div className="font-display leading-none text-white" style={{ fontSize: "clamp(6rem, 14vw, 10rem)" }}>
            <span style={{ color: p1Color }}>V</span>
            <span className="text-white/20">S</span>
          </div>
          <div className="font-mono-kl text-xs uppercase tracking-widest"
            style={{ color: battleMode === "live_avatar" && !avatarConnected ? "var(--kl-red)" : "rgba(255,255,255,0.35)" }}>
            {battleMode === "live_avatar" && !avatarConnected
              ? `Live avatar connecting${dots}`
              : `Battle begins${dots}`}
          </div>
          {battleMode === "live_avatar" && (
            <div className="px-4 py-1.5 rounded-full font-mono-kl text-[10px] uppercase tracking-widest"
              style={{ background: "rgba(230,57,70,0.12)", border: "1px solid rgba(230,57,70,0.3)", color: "var(--kl-red)" }}>
              🎥 Live Avatar Mode
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Unified Question + Answer page — true split-screen, always fits in viewport
// ─────────────────────────────────────────────────────────────────────────────
const LOCAL_AVATARS = {
  chanakya: "/chankya.png",
  ashoka:   "/ashoka.png",
  kalpana:  "/kalpana.png",
  bhawna:   "/bhawna.png",
};
const AGENT_COLORS = {
  chanakya: "#EF9F27", ashoka: "#7F77DD", kalpana: "#1D9E75", bhawna: "#E63946",
};
const OPTION_LETTERS = ["A", "B", "C", "D"];

const TOPIC_GROUP_MAP_B = {
  science:          "A",
  general_knowledge:"A",
  know_your_bihar:  "B",
  hindi_cinema:     "B",
};
const COMBO_AVATAR_B = {
  "A-male-en":   "/male-1.png",
  "A-male-hi":   "/male-2.png",
  "A-female-en": "/fe-1.png",
  "A-female-hi": "/fe-2.png",
  "B-male-en":   "/male-3.png",
  "B-male-hi":   "/male-4.png",
  "B-female-en": "/fe-3.png",
  "B-female-hi": "/fe-4.png",
};
const COMBO_NAME_B = {
  "A-male-en":   "John",   "A-male-hi":   "James",
  "A-female-en": "Emma",   "A-female-hi": "Nimmi",
  "B-male-en":   "Peter",  "B-male-hi":   "Russell",
  "B-female-en": "Bella",  "B-female-hi": "Lilly",
};

function QuestionAndAnswer({
  playerNum, player, opponent,
  agentKey, agentInfo,
  question, options, topic,
  lang, agentGender,
  p1AvatarSrc, p2AvatarSrc,
  p1AgentKey, p2AgentKey,
  battleMode, liveAvatarRef, avatarConnected,
  roundNum,
  selectedOption, onSelect,
  onSubmit, scores,
}) {
  const [subStage, setSubStage]     = useState("speaking");
  const [audioState, setAudioState] = useState("init");
  const timerRef = useRef(null);
  const doneRef  = useRef(false);
  const audioRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(PLAYER_TIME);

  const ringColor = AGENT_COLORS[agentKey] || "#E63946";

  // Use the player's chosen fantasy avatar; fall back to AI agent image
  const myAvatarImg    = (playerNum === 1 ? player  : opponent)?.avatar?.img || (playerNum === 1 ? p1AvatarSrc : p2AvatarSrc);
  const otherAvatarImg = (playerNum === 1 ? opponent : player)?.avatar?.img  || (playerNum === 1 ? p2AvatarSrc : p1AvatarSrc);
  const p1Img = playerNum === 1 ? myAvatarImg : otherAvatarImg;
  const p2Img = playerNum === 2 ? myAvatarImg : otherAvatarImg;

  const finishSpeaking = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setAudioState("done");
    setSubStage("answering");
  };

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    window.speechSynthesis?.cancel();
  };

  const speakNow = useCallback(async () => {
    stopAudio();
    doneRef.current = false;
    setSubStage((prev) => prev === "answering" ? "answering" : "speaking");
    setAudioState("loading");
    const firstName  = player.name.split(" ")[0];
    const spokenText = `${firstName}, ${question}`;

    if (battleMode === "live_avatar" && liveAvatarRef?.current) {
      setAudioState("playing");
      liveAvatarRef.current.speak(spokenText).then(finishSpeaking).catch(finishSpeaking);
      return;
    }

    let audioStarted = false;
    api.agentSpeak(agentKey, lang, spokenText, topic?.id || "", agentGender || "male").then((buf) => {
      if (audioStarted || !buf || !buf.byteLength) return;
      audioStarted = true;
      const blob  = new Blob([buf], { type: "audio/mpeg" });
      const url   = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); audioRef.current = null; finishSpeaking(); };
      audio.onerror = () => { audioRef.current = null; finishSpeaking(); };
      audio.play().then(() => setAudioState("playing")).catch(() => setAudioState("idle"));
    }).catch(() => {
      if (audioStarted) return;
      audioStarted = true;
      const utt = new SpeechSynthesisUtterance(spokenText);
      utt.lang  = lang === "hi" || lang === "bho" ? "hi-IN" : "en-IN";
      utt.rate  = 0.88;
      utt.onend   = finishSpeaking;
      utt.onerror = finishSpeaking;
      window.speechSynthesis.speak(utt);
      setAudioState("playing");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, question, lang, agentKey, agentGender, battleMode, topic]);

  useEffect(() => {
    const id = setTimeout(speakNow, 2000);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (subStage !== "answering") return;
    setTimeLeft(PLAYER_TIME);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(timerRef.current); onSubmit(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStage]);

  useEffect(() => () => {
    stopAudio();
    clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPlaying = audioState === "playing";
  const isLoading = audioState === "loading" || audioState === "init";
  const pct    = (timeLeft / PLAYER_TIME) * 100;
  const stroke = timeLeft <= 8 ? "#E63946" : timeLeft <= 15 ? "#FFB400" : "#FFFFFF";

  // Shared center content: question + options, compact and no-scroll
  const centerContent = (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column", minWidth: 0,
      background: "rgba(8,12,28,0.97)",
      padding: "28px 20px 14px",
      overflow: "hidden",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexShrink: 0 }}>
        <div style={{ fontFamily: "monospace", fontSize: 12, letterSpacing: "0.3em", color: "var(--kl-red)", textTransform: "uppercase" }}>
          Q {roundNum}/{TOTAL_ROUNDS} · {player.name.split(" ")[0]}'s Turn
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, fontFamily: "var(--font-display, sans-serif)", fontSize: 22, fontWeight: 900, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.5)", letterSpacing: "0.1em" }}>{player.name.split(" ")[0]}</span>
            <span style={{ color: "var(--kl-red)", fontSize: 32, lineHeight: 1 }}>{scores?.[`p${playerNum}`] ?? 0}</span>
          </div>
          <span style={{ fontSize: 18, opacity: 0.2 }}>vs</span>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ color: "#7F77DD", fontSize: 32, lineHeight: 1 }}>{scores?.[playerNum === 1 ? "p2" : "p1"] ?? 0}</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.5)", letterSpacing: "0.1em" }}>{opponent.name.split(" ")[0]}</span>
          </div>
        </div>
      </div>

      {/* Opponent wait pill */}
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "4px 12px", borderRadius: 20,
        border: "1px solid rgba(251,191,36,0.35)",
        background: "rgba(251,191,36,0.06)",
        fontFamily: "monospace", fontSize: 11, letterSpacing: "0.2em",
        color: "rgba(251,191,36,0.65)", textTransform: "uppercase",
        marginBottom: 10, flexShrink: 0, alignSelf: "flex-start",
      }}>
        {opponent.name.split(" ")[0]}, please wait
      </div>

      {/* Question box */}
      <div style={{
        borderRadius: 8, border: "1px solid rgba(230,57,70,0.25)",
        background: "rgba(230,57,70,0.04)", padding: "12px 16px",
        marginBottom: 12, flexShrink: 0,
      }}>
        <div style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: "0.3em", color: "var(--kl-red)", textTransform: "uppercase", marginBottom: 6 }}>
          {player.name.split(" ")[0]}'s Question
        </div>
        <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: "clamp(18px, 2.5vw, 24px)", fontWeight: 700, lineHeight: 1.3, color: "#fff", textTransform: "uppercase" }}>
          {question}
        </div>
      </div>

      {/* Speaking indicator */}
      {subStage === "speaking" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexShrink: 0, fontFamily: "monospace", fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: "0.2em", textTransform: "uppercase" }}>
          <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
            {[0,1,2,3].map((i) => (
              <div key={i} style={{ width: 3, height: 12, background: ringColor, borderRadius: 2, opacity: isPlaying ? 1 : 0.3,
                animation: isPlaying ? `sound-bar-qa 0.55s ease-in-out ${i*0.12}s infinite alternate` : "none" }} />
            ))}
          </div>
          {isLoading ? "Preparing…" : isPlaying ? "Agent speaking…" : ""}
          {!isLoading && !isPlaying && (
            <button onClick={speakNow} style={{ color: "rgba(255,255,255,0.5)", background: "none", border: "none", cursor: "pointer", fontSize: 10 }}>↺ Replay</button>
          )}
        </div>
      )}

      {/* Options */}
      {subStage === "answering" && (
        <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, flexShrink: 0, marginBottom: 12 }}>
            {options.map((opt, i) => {
              const letter = OPTION_LETTERS[i];
              const isSelected = selectedOption === letter;
              return (
                <button key={letter} onClick={() => onSelect(letter)}
                  style={{
                    textAlign: "left", padding: "11px 14px", borderRadius: 6,
                    border: isSelected ? "1.5px solid rgba(230,57,70,0.7)" : "1px solid rgba(255,255,255,0.1)",
                    background: isSelected ? "rgba(230,57,70,0.13)" : "rgba(255,255,255,0.02)",
                    cursor: "pointer",
                    animation: `option-appear 0.3s cubic-bezier(0.22,1,0.36,1) ${i * 0.07}s both`,
                  }}>
                  <span style={{ fontFamily: "monospace", fontSize: 11, letterSpacing: "0.25em", color: isSelected ? "var(--kl-red)" : "rgba(255,255,255,0.3)", textTransform: "uppercase", marginRight: 6 }}>{letter}.</span>
                  <span style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: "clamp(14px, 1.7vw, 18px)", fontWeight: 600, color: isSelected ? "#fff" : "rgba(255,255,255,0.75)", textTransform: "uppercase" }}>
                    {opt.replace(/^[A-D]\.\s*/i, "")}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Submit + timer */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <button onClick={onSubmit} disabled={!selectedOption}
              style={{
                flex: 1, padding: "10px 16px", borderRadius: 8,
                background: selectedOption ? "var(--kl-red)" : "rgba(255,255,255,0.06)",
                border: "none", cursor: selectedOption ? "pointer" : "not-allowed",
                color: "#fff", fontFamily: "var(--font-display, sans-serif)",
                fontSize: 15, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                opacity: selectedOption ? 1 : 0.45,
              }}>
              {selectedOption ? `Lock in ${selectedOption}` : "Pick an option"}
            </button>
            <div style={{ position: "relative", width: 52, height: 52, flexShrink: 0 }}>
              <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
                <circle cx="50" cy="50" r="44" stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
                <circle cx="50" cy="50" r="44" stroke={stroke} strokeWidth="8" fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 44}`}
                  strokeDashoffset={`${2 * Math.PI * 44 * (1 - pct / 100)}`}
                  style={{ transition: "stroke-dashoffset 1s linear" }} />
              </svg>
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-display, sans-serif)", fontSize: 18, fontWeight: 700, color: "#fff" }}>{timeLeft}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // ── Live Avatar mode layout ────────────────────────────────────────────────
  if (battleMode === "live_avatar") {
    return (
      <div className="fixed bottom-0 z-20 flex flex-col"
        style={{ top: 65, left: "28vw", right: 0, background: "rgba(8,12,28,0.97)" }}
        data-testid={`p${playerNum}-qa`}>

        {/* Top strip: player avatar cards */}
        <div style={{
          height: 260, flexShrink: 0, display: "flex", alignItems: "center",
          justifyContent: "space-around", padding: "12px 24px 0",
          background: "rgba(6,10,24,0.9)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}>
          {/* P1 avatar card */}
          <div style={{ textAlign: "center", flexShrink: 0, opacity: playerNum === 1 ? 1 : 0.35, transition: "opacity 0.4s" }}>
            <div style={{
              width: 120, height: 175, margin: "0 auto 8px",
              borderRadius: 12, overflow: "hidden",
              border: playerNum === 1 ? "2.5px solid var(--kl-red)" : "2px solid rgba(255,255,255,0.1)",
              boxShadow: playerNum === 1 ? "0 0 28px rgba(230,57,70,0.45)" : "none",
            }}>
              <img src={p1Img} alt="P1" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 8%" }} />
            </div>
            <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 13, color: "#fff", textTransform: "uppercase", fontWeight: 700 }}>{player.name.split(" ")[0]}</div>
            <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 42, color: "var(--kl-red)", fontWeight: 900, lineHeight: 1 }}>{scores?.p1 ?? 0}</div>
            {playerNum === 1 && isPlaying && (
              <div style={{ display: "flex", justifyContent: "center", gap: 3, marginTop: 4 }}>
                {[0,1,2,3].map(i => (
                  <div key={i} style={{ width: 3, height: 12, background: "var(--kl-red)", borderRadius: 2,
                    animation: `sound-bar-qa 0.5s ease-in-out ${i*0.1}s infinite alternate` }} />
                ))}
              </div>
            )}
          </div>

          {/* VS divider */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 32, fontWeight: 900, color: "rgba(255,255,255,0.15)", letterSpacing: "0.1em" }}>VS</div>
            <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 16, fontWeight: 900, color: "#fff", textTransform: "uppercase" }}>
              {player.name.split(" ")[0]}<span style={{ color: "var(--kl-red)" }}>'S TURN</span>
            </div>
            {!avatarConnected && (
              <div style={{ fontFamily: "monospace", fontSize: 8, color: "var(--kl-red)", letterSpacing: "0.3em", textTransform: "uppercase",
                animation: "la-blink 1.4s ease-in-out infinite" }}>
                Connecting live avatar…
              </div>
            )}
          </div>

          {/* P2 avatar card */}
          <div style={{ textAlign: "center", flexShrink: 0, opacity: playerNum === 2 ? 1 : 0.35, transition: "opacity 0.4s" }}>
            <div style={{
              width: 120, height: 175, margin: "0 auto 8px",
              borderRadius: 12, overflow: "hidden",
              border: playerNum === 2 ? "2.5px solid #7F77DD" : "2px solid rgba(255,255,255,0.1)",
              boxShadow: playerNum === 2 ? "0 0 28px rgba(127,119,221,0.45)" : "none",
            }}>
              <img src={p2Img} alt="P2" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 8%" }} />
            </div>
            <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 13, color: "#fff", textTransform: "uppercase", fontWeight: 700 }}>{opponent.name.split(" ")[0]}</div>
            <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 42, color: "#7F77DD", fontWeight: 900, lineHeight: 1 }}>{scores?.p2 ?? 0}</div>
            {playerNum === 2 && isPlaying && (
              <div style={{ display: "flex", justifyContent: "center", gap: 3, marginTop: 4 }}>
                {[0,1,2,3].map(i => (
                  <div key={i} style={{ width: 3, height: 12, background: "#7F77DD", borderRadius: 2,
                    animation: `sound-bar-qa 0.5s ease-in-out ${i*0.1}s infinite alternate` }} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bottom: question + options */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          {centerContent}
        </div>

        <style>{`
          @keyframes sound-bar-qa { from { height: 4px; } to { height: 22px; } }
          @keyframes la-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
          @keyframes option-appear { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </div>
    );
  }

  // ── AI Voice mode — true split-screen, always fits viewport ───────────────
  return (
    <div className="fixed inset-x-0 bottom-0 z-20 flex" style={{ top: 65 }}
      data-testid={`p${playerNum}-qa`}>

      {/* LEFT: P1 full-height avatar */}
      <div style={{
        width: "26%", flexShrink: 0, position: "relative",
        background: "#000", overflow: "hidden",
        opacity: playerNum === 1 ? 1 : 0.4, transition: "opacity 0.4s",
      }}>
        <img src={p1Img} alt="P1"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }} />
        {/* Right fade */}
        <div style={{ position: "absolute", inset: 0,
          background: "linear-gradient(to right, transparent 55%, rgba(8,12,28,0.95) 100%)", pointerEvents: "none" }} />
        {/* Glow when active */}
        {playerNum === 1 && (
          <div style={{ position: "absolute", inset: 0,
            boxShadow: `inset 0 0 50px ${AGENT_COLORS[p1AgentKey] || "#E63946"}50`, pointerEvents: "none" }} />
        )}
        {/* Sound bars */}
        {playerNum === 1 && isPlaying && (
          <div style={{ position: "absolute", bottom: 60, left: 0, right: 0,
            display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 3 }}>
            {[0,1,2,3,4].map((i) => (
              <div key={i} style={{ width: 3, borderRadius: 2, background: AGENT_COLORS[p1AgentKey] || "#E63946",
                animation: `sound-bar-qa 0.55s ease-in-out ${i*0.1}s infinite alternate`, minHeight: 3 }} />
            ))}
          </div>
        )}
        {/* Name + score */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0,
          background: "linear-gradient(transparent, rgba(6,10,31,0.97))",
          padding: "28px 8px 10px", textAlign: "center" }}>
          <div style={{ fontFamily: "monospace", fontSize: 8, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.2em" }}>Player 1</div>
          <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 12, color: "#fff", textTransform: "uppercase", fontWeight: 700 }}>{player.name.split(" ")[0]}</div>
          <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 26, fontWeight: 900, color: AGENT_COLORS[p1AgentKey] || "#E63946" }}>{scores?.p1 ?? 0}</div>
        </div>
      </div>

      {/* CENTER: question + options (no scroll) */}
      {centerContent}

      {/* RIGHT: P2 full-height avatar */}
      <div style={{
        width: "26%", flexShrink: 0, position: "relative",
        background: "#000", overflow: "hidden",
        opacity: playerNum === 2 ? 1 : 0.4, transition: "opacity 0.4s",
      }}>
        <img src={p2Img} alt="P2"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }} />
        {/* Left fade */}
        <div style={{ position: "absolute", inset: 0,
          background: "linear-gradient(to left, transparent 55%, rgba(8,12,28,0.95) 100%)", pointerEvents: "none" }} />
        {playerNum === 2 && (
          <div style={{ position: "absolute", inset: 0,
            boxShadow: `inset 0 0 50px ${AGENT_COLORS[p2AgentKey] || "#7F77DD"}50`, pointerEvents: "none" }} />
        )}
        {playerNum === 2 && isPlaying && (
          <div style={{ position: "absolute", bottom: 60, left: 0, right: 0,
            display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 3 }}>
            {[0,1,2,3,4].map((i) => (
              <div key={i} style={{ width: 3, borderRadius: 2, background: AGENT_COLORS[p2AgentKey] || "#7F77DD",
                animation: `sound-bar-qa 0.55s ease-in-out ${i*0.1}s infinite alternate`, minHeight: 3 }} />
            ))}
          </div>
        )}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0,
          background: "linear-gradient(transparent, rgba(6,10,31,0.97))",
          padding: "28px 8px 10px", textAlign: "center" }}>
          <div style={{ fontFamily: "monospace", fontSize: 8, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.2em" }}>Player 2</div>
          <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 12, color: "#fff", textTransform: "uppercase", fontWeight: 700 }}>{opponent.name.split(" ")[0]}</div>
          <div style={{ fontFamily: "var(--font-display, sans-serif)", fontSize: 26, fontWeight: 900, color: AGENT_COLORS[p2AgentKey] || "#7F77DD" }}>{scores?.p2 ?? 0}</div>
        </div>
      </div>

      <style>{`
        @keyframes sound-bar-qa { from { height: 4px; } to { height: 22px; } }
        @keyframes option-appear { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
// Scoreboard — compact strip
// ─────────────────────────────────────────────
function AvatarBadge({ avatar, size = 10 }) {
  const px = size * 4; // tailwind size → px (w-10 = 40px)
  if (!avatar?.img) return (
    <div style={{ width: px, height: px }} className="rounded-full bg-white/10 flex items-center justify-center text-sm shrink-0">?</div>
  );
  return (
    <div style={{ width: px, height: px, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
      <img src={avatar.img} alt={avatar.label || "avatar"}
        style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }} />
    </div>
  );
}

function Scoreboard({ p1, p2, scores, round, p1Topic, p2Topic }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-center relative z-10" data-testid="battle-scoreboard">
      {/* P1 */}
      <div className="rounded-md border border-[color:var(--kl-red)]/30 px-3 py-2 flex items-center gap-2"
        style={{ background: "rgba(230,57,70,0.08)" }}>
        <AvatarBadge avatar={p1.avatar} size={10} />
        <div className="min-w-0 flex-1">
          <div className="font-mono-kl text-[8px] uppercase tracking-widest text-[color:var(--kl-red)]">Player 1</div>
          <div className="font-display text-base uppercase leading-tight text-white truncate">{p1.name}</div>
          {p1Topic && <div className="font-mono-kl text-[8px] uppercase tracking-widest text-white/30 truncate">{p1Topic}</div>}
        </div>
        <div className="font-display text-4xl text-[color:var(--kl-red)] shrink-0" data-testid="p1-score">{scores.p1}</div>
      </div>
      {/* Centre */}
      <div className="rounded-md border border-white/15 px-3 py-2 text-center"
        style={{ background: "rgba(255,255,255,0.04)" }}>
        <div className="font-mono-kl text-[8px] uppercase tracking-widest text-white/40">Question</div>
        <div className="font-display text-3xl leading-tight text-white">
          {round} <span className="text-white/30 text-base">/ {TOTAL_ROUNDS}</span>
        </div>
      </div>
      {/* P2 */}
      <div className="rounded-md border border-white/15 px-3 py-2 flex items-center gap-2"
        style={{ background: "rgba(255,255,255,0.03)" }}>
        <div className="font-display text-4xl text-white shrink-0" data-testid="p2-score">{scores.p2}</div>
        <div className="min-w-0 flex-1 text-right">
          <div className="font-mono-kl text-[8px] uppercase tracking-widest text-white/40">Player 2</div>
          <div className="font-display text-base uppercase leading-tight text-white truncate">{p2.name}</div>
          {p2Topic && <div className="font-mono-kl text-[8px] uppercase tracking-widest text-white/30 truncate">{p2Topic}</div>}
        </div>
        <AvatarBadge avatar={p2.avatar} size={10} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Topic picker
// ─────────────────────────────────────────────
function TopicPicker({ stage, p1, p2, p1Topic, topics, lang, agentGender, battleMode, onPick }) {
  const isP1   = stage === "p1_topic";
  const player = isP1 ? p1 : p2;
  const langLabel = lang === "hi" ? "हिन्दी" : lang === "bho" ? "भोजपुरी" : "English";
  return (
    <div className="mt-10 relative z-10 slide-up" data-testid={`battle-${stage}-stage`}>
      {/* Mode + language badge */}
      <div className="mb-4 flex gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20 font-mono-kl text-[10px] uppercase tracking-widest text-white/50"
          style={{ background: "rgba(255,255,255,0.04)" }}>
          {battleMode === "live_avatar" ? "🎥 Live Avatar · EN" : `🎙 AI Voice · ${langLabel}`}
        </span>
      </div>
      {!isP1 && p1Topic && (
        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kl-red)]/40 font-mono-kl text-xs uppercase tracking-widest text-[color:var(--kl-red)]"
          style={{ background: "rgba(230,57,70,0.08)" }}>
          {p1.name.split(" ")[0]} chose: {p1Topic.label}
        </div>
      )}
      <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">
        <span className="kl-accent-line mr-3" />{player.name.toUpperCase()} · PICK YOUR FIELD
      </div>
      <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight mt-2 text-white">
        {player.name.split(" ")[0]},<br />
        <span className="text-[color:var(--kl-red)]">your battlefield.</span>
      </h1>
      <p className="text-white/60 mt-4">
        {isP1
          ? `${p1.name.split(" ")[0]}, pick your topic. ${p2.name.split(" ")[0]} picks next.`
          : `${p2.name.split(" ")[0]}, pick your topic. You can choose differently from ${p1.name.split(" ")[0]}.`}
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-9">
        {topics.map((t) => {
          const Icon = TOPIC_ICONS[t.icon] || TOPIC_ICONS.globe;
          return (
            <button key={t.id} onClick={() => onPick(t)} data-testid={`battle-topic-${t.id}`}
              className="group text-left p-9 rounded-md border border-white/10 hover:border-[color:var(--kl-red)] transition-all flex flex-col"
              style={{ background: "rgba(255,255,255,0.02)", minHeight: 380 }}>
              <Icon className="w-12 h-12 text-[color:var(--kl-red)] mb-6" />
              <div className="font-display text-3xl uppercase leading-tight text-white">{t.label}</div>
              <div className="text-base text-white/55 mt-4 flex-1 leading-relaxed">{t.description}</div>
              <div className="flex items-center gap-1.5 text-sm font-mono-kl uppercase tracking-widest text-[color:var(--kl-red)] mt-8 group-hover:translate-x-1 transition-transform">
                Choose <ArrowUpRight className="w-4 h-4" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Winner screen — tournament-aware
// ─────────────────────────────────────────────
function WinnerScreen({ winner, p1, p2, onClaim, navigate }) {
  const isTie        = winner.winner_num === 0;
  const winnerPlayer = winner.winner_num === 1 ? p1 : winner.winner_num === 2 ? p2 : null;
  const loserPlayer  = winner.winner_num === 1 ? p2 : winner.winner_num === 2 ? p1 : null;

  const level      = winner.tournament_level;
  const levelLabel = LEVEL_LABELS[level] || (isTie ? "" : "Match");
  const nextLevel  = LEVEL_NEXT[level];
  const isFinal    = level === "L2";
  const isBronze   = level === "L3";
  const isL1       = level === "L1";
  const isTournamentMatch = !!winner.tournament_id;

  const winnerProgressMsg = isFinal   ? "Tournament Champion!"
    : isBronze  ? "Finishes 3rd — Well fought!"
    : nextLevel ? `Through to the ${nextLevel}!`
    : isTournamentMatch ? "Advances to the next round!"
    : null;

  const roundLabel = isTournamentMatch && levelLabel
    ? `${winner.tournament_name} · ${levelLabel}`
    : levelLabel || null;

  const loserProgressMsg = isL1 ? "Plays the Bronze Match" : "Better luck next time";

  // ── Confetti burst every 2 seconds while on this screen ──
  useEffect(() => {
    const burst = () => {
      confetti({ particleCount: 80, spread: 100, origin: { y: 0.4 }, colors: ["#e63946", "#ffffff", "#ffd700", "#0047ab"] });
      confetti({ particleCount: 40, spread: 60, angle: 60, origin: { x: 0, y: 0.5 }, colors: ["#e63946", "#fff", "#ffd700"] });
      confetti({ particleCount: 40, spread: 60, angle: 120, origin: { x: 1, y: 0.5 }, colors: ["#e63946", "#fff", "#ffd700"] });
    };
    burst(); // immediately on mount
    const id = setInterval(burst, 2000);
    return () => { clearInterval(id); confetti.reset(); };
  }, []);

  // Build shareable URL
  const shareUrl = (() => {
    const base = `${window.location.origin}/winner-share`;
    const sp = new URLSearchParams();
    if (!isTie && winnerPlayer) {
      sp.set("name",   winnerPlayer.name);
      sp.set("score",  String(winner.winner_num === 1 ? winner.p1_total : winner.p2_total));
      sp.set("opp",    loserPlayer?.name || "");
      sp.set("oscore", String(winner.winner_num === 1 ? winner.p2_total : winner.p1_total));
    }
    if (level) sp.set("level", level);
    if (isTournamentMatch && winner.tournament_name) sp.set("t", winner.tournament_name);
    if (winnerPlayer?.avatar?.img) sp.set("avatar", encodeURIComponent(winnerPlayer.avatar.img));
    return `${base}?${sp.toString()}`;
  })();

  const [linkCopied, setLinkCopied] = useState(false);
  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div className="relative z-10 slide-up" data-testid="winner-screen"
      style={{ display: "flex", alignItems: "stretch", minHeight: 520 }}>

      {/* ── LEFT: Full-height winner avatar — wider, no overlays ── */}
      <div className="hidden lg:block shrink-0" style={{ width: 280 }}>
        {!isTie && winnerPlayer?.avatar?.img ? (
          <div style={{ width: "100%", height: "100%", minHeight: 520, position: "relative", overflow: "hidden" }}>
            <img src={winnerPlayer.avatar.img} alt={winnerPlayer.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }} />
            {/* Only a soft bottom fade for the name — NO red overlay */}
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              background: "linear-gradient(transparent 30%, rgba(6,10,31,0.98))",
              padding: "48px 16px 16px", textAlign: "center",
            }}>
              <div className="font-display text-xl uppercase text-white tracking-wide">{winnerPlayer.name}</div>
              <div className="font-mono-kl text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--kl-red)" }}>
                {isFinal ? "Champion" : isBronze ? "3rd Place" : "Winner"}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ height: "100%", minHeight: 520, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Trophy className="w-20 h-20 text-[color:var(--kl-red)]" />
          </div>
        )}
      </div>

      {/* ── RIGHT: Result info — generous left padding ── */}
      <div className="flex-1 flex flex-col justify-center pl-0 lg:pl-14 xl:pl-20 py-8 min-w-0">

        {/* Tournament + round badge */}
        {roundLabel && (
          <div className="mb-5">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[color:var(--kl-red)]/40 font-mono-kl text-xs uppercase tracking-widest text-[color:var(--kl-red)]"
              style={{ background: "rgba(230,57,70,0.07)" }}>
              {roundLabel}
            </span>
          </div>
        )}

        {/* Result label */}
        <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-2">
          {isTie ? "It's a Tie" : isFinal ? "Tournament Champion" : isBronze ? "3rd Place" : "Match Winner"}
        </div>

        {/* BIG NAME + WINS. on one line */}
        <h2 className="font-display uppercase tracking-tight leading-[0.88]"
          style={{ fontSize: "clamp(3.5rem, 8vw, 6.5rem)" }}>
          {isTie ? (
            <><span className="text-white">It's a </span><span style={{ color: "var(--kl-red)" }}>TIE!</span></>
          ) : (
            <><span className="text-white">{winnerPlayer.name.split(" ")[0]} </span><span style={{ color: "var(--kl-red)" }}>WINS.</span></>
          )}
        </h2>

        {/* Score line with mini avatars */}
        <div className="flex items-center gap-3 mt-5">
          {p1.avatar?.img && (
            <div style={{ width: 30, height: 42, borderRadius: 5, overflow: "hidden", flexShrink: 0 }}>
              <img src={p1.avatar.img} alt={p1.name} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
            </div>
          )}
          <div className="font-display text-2xl md:text-3xl text-white/80">
            {p1.name.split(" ")[0]}{" "}
            <span style={{ color: "var(--kl-red)" }}>{winner.p1_total}</span>
            <span className="text-white/25 mx-2">·</span>
            <span style={{ color: "var(--kl-red)" }}>{winner.p2_total}</span>{" "}
            {p2.name.split(" ")[0]}
          </div>
          {p2.avatar?.img && (
            <div style={{ width: 30, height: 42, borderRadius: 5, overflow: "hidden", flexShrink: 0 }}>
              <img src={p2.avatar.img} alt={p2.name} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
            </div>
          )}
        </div>

        {/* Progress cards */}
        {!isTie && (
          <div className="flex flex-col sm:flex-row gap-3 mt-6 max-w-lg">
            <div className="flex-1 rounded-md border border-[color:var(--kl-red)]/40 px-4 py-3"
              style={{ background: "rgba(230,57,70,0.08)" }}>
              <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)] mb-1">{winnerPlayer?.name}</div>
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[color:var(--kl-red)] shrink-0" />
                <span className="font-display text-base uppercase text-white leading-tight">{winnerProgressMsg || "Match Winner"}</span>
              </div>
              {nextLevel && <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/35 mt-1">Next up: {nextLevel}</div>}
            </div>
            {loserPlayer && (
              <div className="flex-1 rounded-md border border-white/10 px-4 py-3" style={{ background: "rgba(255,255,255,0.02)" }}>
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40 mb-1">{loserPlayer.name}</div>
                <div className="flex items-center gap-2">
                  <span className="text-base">{isL1 ? "🥉" : "✕"}</span>
                  <span className="font-display text-base uppercase text-white/50 leading-tight">{loserProgressMsg}</span>
                </div>
                {isL1 && <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/25 mt-1">Wait for admin to start Bronze Match</div>}
              </div>
            )}
          </div>
        )}

        {/* QR code + share */}
        <div className="flex items-start gap-4 mt-6 p-4 rounded-md max-w-sm"
          style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
          <div className="shrink-0 p-1.5 rounded-md bg-white" style={{ lineHeight: 0 }}>
            <QRCodeSVG value={shareUrl} size={76} bgColor="#ffffff" fgColor="#06081a" level="M" />
          </div>
          <div className="flex flex-col justify-center gap-1.5">
            <div className="font-mono-kl text-[9px] uppercase tracking-widest" style={{ color: "var(--kl-red)" }}>Share your win</div>
            <div className="font-mono-kl text-[10px] text-white/55 leading-relaxed">
              Scan this QR code to share your result with avatar &amp; score on social media.
            </div>
            <button onClick={copyLink}
              className="font-mono-kl text-[9px] uppercase tracking-widest transition text-left"
              style={{ color: linkCopied ? "#4ade80" : "rgba(255,255,255,0.35)" }}>
              {linkCopied ? "✓ Link copied!" : "Copy link →"}
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 mt-5">
          {(!isTournamentMatch || isFinal || isBronze) && !isTie && (
            <button onClick={onClaim} className="btn-submit" data-testid="claim-gift-btn">
              Claim your Kiba Labs gift <ArrowUpRight className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => navigate("/")} className="btn-ghost-dark" data-testid="winner-home">
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
}

function ClaimScreen({ winner, p1, p2, config, claim, setClaim, submitClaim, claimDone, navigate }) {
  const winnerPlayer = winner.winner_num === 1 ? p1 : p2;
  return (
    <div className="mt-10 max-w-3xl mx-auto relative z-10 slide-up" data-testid="claim-screen">
      <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">
        <span className="kl-accent-line mr-3" />GIFT CLAIM
      </div>
      <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] mt-2 text-white">
        Hello <span className="text-[color:var(--kl-red)]">{winnerPlayer.name.split(" ")[0]}</span>.<br />
        One last step.
      </h2>
      {!claimDone ? (
        <>
          <p className="mt-4 text-white/60 text-lg">Complete both steps to unlock your gift.</p>
          <Step n="01" t="Follow Kiba Labs on LinkedIn" href={config.linkedin_url} done={claim.linkedin}
            onCheck={(v) => setClaim({ ...claim, linkedin: v })} testid="step-linkedin" />
          <Step n="02" t="Join Kiba Labs WhatsApp Community" href={config.whatsapp_url} done={claim.whatsapp}
            onCheck={(v) => setClaim({ ...claim, whatsapp: v })} testid="step-whatsapp" />
          <button
            disabled={!(claim.linkedin && claim.whatsapp)}
            onClick={() => submitClaim(winner.winner_num)}
            className="btn-submit mt-7 text-lg w-full justify-center"
            data-testid="submit-claim">
            {claim.linkedin && claim.whatsapp ? "Claim Your Gift" : "Complete both steps first"}
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </>
      ) : (
        <div className="mt-6 rounded-md border border-[color:var(--kl-red)]/40 p-8" data-testid="claim-done"
          style={{ background: "rgba(230,57,70,0.08)" }}>
          <div className="font-display text-4xl uppercase text-white">Gift Locked In!</div>
          <p className="mt-3 text-lg text-white/80">Your details are with the Kiba Labs team. Show this screen at our booth to collect your gift.</p>
          <button onClick={() => navigate("/")} className="btn-submit mt-5">Back to home</button>
        </div>
      )}
    </div>
  );
}

function Step({ n, t, href, done, onCheck, testid }) {
  return (
    <div className="mt-5 p-5 rounded-md border transition" style={{
      borderColor: done ? "rgba(230,57,70,0.5)" : "rgba(255,255,255,0.1)",
      background:  done ? "rgba(230,57,70,0.06)" : "rgba(255,255,255,0.02)",
    }} data-testid={testid}>
      <div className="font-mono-kl text-xs uppercase tracking-widest text-white/40">Step {n}</div>
      <div className="font-display text-2xl uppercase mt-1 text-white">{t}</div>
      <div className="flex flex-wrap items-center gap-4 mt-4">
        <a href={href || "#"} target="_blank" rel="noreferrer" className="btn-submit text-sm" data-testid={`${testid}-open`}>
          Open Link <ArrowUpRight className="w-4 h-4" />
        </a>
        <label className="flex items-center gap-2 font-mono-kl text-xs uppercase tracking-widest cursor-pointer text-white/70">
          <input type="checkbox" checked={done} onChange={(e) => onCheck(e.target.checked)}
            className="w-5 h-5 accent-[color:var(--kl-red)]" data-testid={`${testid}-check`} />
          I have completed this step
        </label>
      </div>
    </div>
  );
}
